<script lang="ts">
  import { api, type DealPipeline } from "../lib/api";
  import { haptic } from "../lib/tg";

  let {
    open = $bindable(false),
    role = "unknown",
    oncreated,
  }: {
    open?: boolean;
    role?: string;
    oncreated?: (leadAmoId: number, name: string) => void;
  } = $props();

  type Member = { amo_id: number; name: string; is_me: boolean };

  let pipelines = $state<DealPipeline[]>([]);
  let team = $state<Member[]>([]);
  let loaded = false;

  let contactName = $state("");
  let phone = $state("");
  let name = $state("");
  let price = $state("");
  let pipelineId = $state<number | undefined>();
  let statusId = $state<number | undefined>();
  let responsibleId = $state<number | undefined>();
  let withTask = $state(true);
  let taskText = $state("Связаться с клиентом");
  let due = $state("tomorrow_10");
  let saving = $state(false);
  let error = $state("");

  const isDirector = $derived(role === "director");

  // Stages a new deal can start on: not closed, not amo's «Неразобранное» inbox.
  const stages = $derived(
    (pipelines.find((p) => p.amo_id === pipelineId)?.statuses ?? []).filter(
      (s) => s.amo_id !== 142 && s.amo_id !== 143 && s.name.trim().toLowerCase() !== "неразобранное",
    ),
  );

  $effect(() => {
    if (!open || loaded) return;
    loaded = true;
    api.dealsMeta().then((m) => {
      pipelines = m.pipelines;
      const main = m.pipelines.find((p) => p.is_main) ?? m.pipelines[0];
      pipelineId = main?.amo_id;
    }).catch(() => { error = "Не загрузились воронки — закройте и откройте снова"; loaded = false; });
    if (isDirector) {
      api.team().then((t) => {
        team = t.items;
        responsibleId = (t.items.find((x) => x.is_me) ?? t.items[0])?.amo_id;
      }).catch(() => {});
    }
  });

  $effect(() => {
    // keep the stage valid when the pipeline changes
    if (stages.length && !stages.some((s) => s.amo_id === statusId)) statusId = stages[0].amo_id;
  });

  function mskDay(offsetDays: number): string {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Moscow" })
      .format(new Date(Date.now() + offsetDays * 86400000));
  }
  const mskHourNow = () =>
    Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Moscow", hour: "2-digit", hour12: false }).format(new Date()));

  const dueOptions = $derived([
    ...(mskHourNow() < 17 ? [["today_18", "Сегодня 18:00"]] : []),
    ["tomorrow_10", "Завтра 10:00"],
    ["tomorrow_18", "Завтра 18:00"],
    ["in_3", "Через 3 дня"],
  ] as [string, string][]);

  function dueIso(): string {
    const [day, hour] =
      due === "today_18" ? [mskDay(0), 18] :
      due === "tomorrow_18" ? [mskDay(1), 18] :
      due === "in_3" ? [mskDay(3), 10] : [mskDay(1), 10];
    return `${day}T${String(hour).padStart(2, "0")}:00:00+03:00`;
  }

  const finalName = $derived(name.trim() || contactName.trim());
  const canSave = $derived(!!finalName && !!statusId && !saving && (!withTask || !!taskText.trim()));

  function reset() {
    contactName = ""; phone = ""; name = ""; price = "";
    withTask = true; taskText = "Связаться с клиентом"; due = "tomorrow_10";
    error = "";
  }

  function close() {
    if (saving) return;
    open = false;
  }

  async function save() {
    if (!canSave) return;
    saving = true;
    error = "";
    haptic("medium");
    try {
      const priceNum = parseInt(price.replace(/\D/g, ""), 10);
      const res = await api.createDeal({
        name: finalName,
        pipeline_amo_id: pipelineId,
        status_amo_id: statusId,
        price: Number.isFinite(priceNum) && priceNum > 0 ? priceNum : undefined,
        responsible_user_amo_id: isDirector ? responsibleId : undefined,
        contact_name: contactName.trim() || undefined,
        phone: phone.trim() || undefined,
        task_text: withTask ? taskText.trim() : undefined,
        task_complete_till_iso: withTask ? dueIso() : undefined,
      });
      open = false;
      reset();
      oncreated?.(res.lead_amo_id, res.name);
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      saving = false;
    }
  }
</script>

{#if open}
  <div class="backdrop" onclick={close} role="presentation"></div>
  <div class="sheet" role="dialog" aria-label="Новая сделка">
    <div class="grab"></div>
    <div class="title">Новая сделка</div>

    <div class="row2">
      <label>
        <span class="lbl">Клиент</span>
        <input bind:value={contactName} placeholder="Карина" autocomplete="off" />
      </label>
      <label>
        <span class="lbl">Телефон</span>
        <input bind:value={phone} type="tel" inputmode="tel" placeholder="+7 925 …" autocomplete="off" />
      </label>
    </div>

    <label>
      <span class="lbl">Название сделки</span>
      <input bind:value={name} placeholder={contactName.trim() ? `${contactName.trim()} — камин` : "Карина — камин"} autocomplete="off" />
    </label>

    <label>
      <span class="lbl">Бюджет, ₽</span>
      <input bind:value={price} inputmode="numeric" placeholder="необязательно" autocomplete="off" />
    </label>

    {#if pipelines.length > 1}
      <div class="lbl">Воронка</div>
      <div class="chips">
        {#each pipelines as p}
          <button class="chip" class:active={pipelineId === p.amo_id} onclick={() => (pipelineId = p.amo_id)} type="button">{p.name}</button>
        {/each}
      </div>
    {/if}

    <div class="lbl">Этап</div>
    <div class="chips">
      {#each stages as s}
        <button class="chip" class:active={statusId === s.amo_id} onclick={() => (statusId = s.amo_id)} type="button">{s.name}</button>
      {:else}
        <span class="muted">загружаю…</span>
      {/each}
    </div>

    {#if isDirector && team.length}
      <div class="lbl">Ответственный</div>
      <div class="chips">
        {#each team as t}
          <button class="chip" class:active={responsibleId === t.amo_id} onclick={() => (responsibleId = t.amo_id)} type="button">{t.name}{t.is_me ? " (я)" : ""}</button>
        {/each}
      </div>
    {/if}

    <div class="lbl task-head">
      <span>Первая задача</span>
      <button class="link" onclick={() => (withTask = !withTask)} type="button">{withTask ? "без задачи" : "добавить"}</button>
    </div>
    {#if withTask}
      <input bind:value={taskText} autocomplete="off" />
      <div class="chips due">
        {#each dueOptions as [val, label]}
          <button class="chip" class:active={due === val} onclick={() => (due = val)} type="button">{label}</button>
        {/each}
      </div>
    {/if}

    {#if error}<div class="err">{error}</div>{/if}

    <div class="actions">
      <button class="btn ghost" onclick={close} type="button" disabled={saving}>Отмена</button>
      <button class="btn primary" onclick={save} disabled={!canSave} type="button">
        {saving ? "Создаю…" : "Создать в amoCRM"}
      </button>
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 60;
    animation: fade 160ms ease;
  }
  .sheet {
    position: fixed; left: 0; right: 0; bottom: 0;
    max-width: 480px; margin: 0 auto;
    background: var(--bg-elev);
    border-top-left-radius: 22px; border-top-right-radius: 22px;
    padding: 12px 18px calc(18px + var(--safe-bottom, 0px));
    box-shadow: var(--shadow-lg);
    z-index: 70;
    animation: slide 220ms cubic-bezier(.18,.89,.32,1.05);
    max-height: 90vh; overflow-y: auto;
  }
  .grab { width: 36px; height: 4px; background: var(--border-strong); border-radius: 2px; margin: 0 auto 12px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-weight: 700; font-size: 15px;
    color: var(--text); margin-bottom: 4px;
  }
  label { display: block; min-width: 0; }
  .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .lbl {
    display: block;
    font-size: 11px; color: var(--text-dim);
    text-transform: uppercase; letter-spacing: 0.08em;
    font-weight: 600;
    margin-top: 12px; margin-bottom: 6px;
  }
  .task-head { display: flex; justify-content: space-between; align-items: baseline; }
  .link {
    background: none; border: none; padding: 0;
    color: var(--ember); font-size: 12px; font-weight: 600;
    text-transform: none; letter-spacing: 0; cursor: pointer;
  }
  input {
    width: 100%; box-sizing: border-box;
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
    color: var(--text);
    font-family: inherit; font-size: 16px; /* 16px: iOS doesn't zoom on focus */
  }
  input:focus { border-color: var(--ember); outline: none; }
  .chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .chips.due { margin-top: 8px; }
  .chip {
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 8px 12px;
    color: var(--text); font-size: 13px;
    cursor: pointer;
    transition: background 120ms, border-color 120ms, transform 120ms;
  }
  .chip:active { transform: scale(0.96); }
  .chip.active { background: var(--ember); color: #fff; border-color: var(--ember); font-weight: 600; }
  .muted { font-size: 13px; color: var(--text-mute); }
  .err {
    margin-top: 12px;
    font-size: 13px; color: var(--danger);
  }
  .actions { display: grid; grid-template-columns: 1fr 1.6fr; gap: 8px; margin-top: 16px; }
  .btn {
    height: 46px;
    border-radius: 12px;
    font-family: 'Manrope', sans-serif;
    font-weight: 600; font-size: 14px;
    cursor: pointer;
    transition: transform 120ms;
  }
  .btn:active:not(:disabled) { transform: scale(0.97); }
  .btn.ghost { background: var(--bg-elev-2); color: var(--text); border: 1px solid var(--border); }
  .btn.primary { background: var(--ember); color: #fff; border: none; box-shadow: var(--shadow-sm); }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; }

  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slide { from { transform: translateY(100%); } to { transform: translateY(0); } }
  @media (prefers-reduced-motion: reduce) {
    .backdrop, .sheet { animation: none; }
  }
</style>
